// Input sanitisation helpers used in submit handlers, payload
// builders, and search/filter inputs.
//
// Design rules:
//   - Sanitisers DO NOT validate; they only normalise. A separately
//     called validator decides if the cleaned value is acceptable.
//   - Strings only. Non-strings are coerced to '' (or returned as-is
//     for special types like the password helper that must NOT
//     mutate its argument silently).
//   - Never log raw values from these helpers — the caller may pass
//     credentials or secrets, and we don't know which.
//
// The matching validators live in utils/validation.js; the AntD form
// rule builders live in utils/formRules.js. Together they form the
// "sanitize -> validate -> submit" pipeline used by submit handlers.

// Strip C0 / C1 control chars + the most common zero-width / bidi
// formatting chars. Common copy-paste hazards from PDFs, Word docs,
// and printed certificates.
//
// Ranges (written as \u escapes for editor safety):
//     -   C0 control + tab/CR/LF (CR/LF survive .trim() too)
//              DEL
//    -   C1 control
//   ​ - ‏  zero-width space, LRM, RLM, ZWJ, ZWNJ
//   ‪ - ‮  bidi overrides
//   ⁠           word joiner
//   ﻿           BOM / zero-width no-break space
const CONTROL_CHARS_RE =
  /[ --​-‏‪-‮⁠﻿]/g;

export function stripControlChars(value) {
  if (typeof value !== 'string') return '';
  return value.replace(CONTROL_CHARS_RE, '');
}

// Trim safely without throwing on non-strings.
export function safeTrim(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

// Collapse runs of whitespace (spaces, tabs, NBSP, etc.) to a single
// ASCII space. Useful for names / addresses where double-typing is
// common.
const WHITESPACE_RE = /\s+/g;

export function normalizeWhitespace(value) {
  if (typeof value !== 'string') return '';
  return value.replace(WHITESPACE_RE, ' ');
}

// Generic text sanitiser. Defaults to a "type-anything" string with
// control chars stripped + edges trimmed; opt into whitespace
// collapsing or maxLength when appropriate. Does NOT lower/upper case.
export function sanitizeText(value, options = {}) {
  let v = stripControlChars(value);
  v = v.trim();
  if (options.collapseWhitespace) v = v.replace(WHITESPACE_RE, ' ');
  if (typeof options.maxLength === 'number' && options.maxLength > 0) {
    v = v.slice(0, options.maxLength);
  }
  return v;
}

export function sanitizeEmail(value) {
  return sanitizeText(value).toLowerCase();
}

// GSTIN: 15 chars, uppercase, alphanumeric only. Strips any spaces /
// dashes a user may have pasted from a printed certificate.
export function sanitizeGstin(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(CONTROL_CHARS_RE, '')
    .replace(/[\s-]+/g, '')
    .toUpperCase()
    .slice(0, 15);
}

// Indian mobile: keep digits only, drop optional +91 / 91 prefix,
// truncate to 10. Backend's MOBILE_RE wants exactly 10 digits.
export function sanitizeMobile(value) {
  if (typeof value !== 'string') return '';
  let digits = value.replace(/\D+/g, '');
  if (digits.length > 10 && digits.startsWith('91')) {
    digits = digits.slice(digits.length - 10);
  }
  return digits.slice(0, 10);
}

// OTP: digits only, max 8 (backend uses 6 today; allow 4–8 to keep
// the helper future-proof).
export function sanitizeOtp(value) {
  if (typeof value !== 'string') return '';
  return value.replace(/\D+/g, '').slice(0, 8);
}

// Username (master / sub-user): lowercase, trim, strip control chars.
// We deliberately do NOT strip uppercase letters here — leave that to
// the validator so the user gets a clear "must be lowercase" error
// instead of silent mutation that hides their typo.
export function sanitizeUsername(value) {
  return sanitizeText(value).toLowerCase();
}

// Search box content. Trim + strip control chars; collapse repeated
// whitespace; cap at 200 chars to keep API payloads tight.
export function sanitizeSearch(value) {
  return sanitizeText(value, { collapseWhitespace: true, maxLength: 200 });
}

// Generic safe code / id field — letters, digits, dot, dash,
// underscore. Used for free-text reference inputs (invoice number,
// transaction code searches). Does not validate; only restricts.
export function sanitizeCode(value) {
  if (typeof value !== 'string') return '';
  return stripControlChars(value).replace(/[^A-Za-z0-9._-]+/g, '').slice(0, 100);
}
